import React, { useRef, useState } from 'react';
import { useEditor, EditorContent, Node, mergeAttributes, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
  Quote,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

interface TipTapEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  /**
   * Téléverse une image et rend l'URL à insérer, ou `null` si l'envoi a
   * échoué (l'appelant a déjà prévenu l'utilisateur). Absent : pas de bouton
   * image, et une image collée ou déposée est ignorée.
   */
  onUploadImage?: (file: File) => Promise<string | null>;
}

/**
 * Image en bloc, citée par URL.
 *
 * Écrite ici plutôt que tirée de `@tiptap/extension-image` : trente lignes
 * contre une dépendance de plus. Une image en `data:` (base64) n'est jamais
 * acceptée : Gmail et Outlook la bloquent, l'organisateur croirait l'avoir
 * envoyée alors que personne ne la verrait.
 */
const ImageBloc = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: '' },
    };
  },
  parseHTML() {
    return [{
      tag: 'img[src]',
      getAttrs: (el) => ((el as HTMLElement).getAttribute('src') || '').startsWith('data:') ? false : null,
    }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes, { class: 'max-w-full h-auto rounded-[3px] my-2' })];
  },
});

function imagesDuTransfert(data: DataTransfer | null): File[] {
  return Array.from(data?.files || []).filter(f => f.type.startsWith('image/'));
}

const ToolbarButton: React.FC<{
  onClick: () => void;
  isActive?: boolean;
  children: React.ReactNode;
  title?: string;
}> = ({ onClick, isActive, children, title }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    onClick={onClick}
    title={title}
    className={cn(
      'h-8 w-8 p-0',
      isActive && 'bg-white/[0.10] text-foreground'
    )}
  >
    {children}
  </Button>
);

const ToolbarDivider = () => (
  <div className="w-px h-6 bg-border mx-1" />
);

const TipTapEditor: React.FC<TipTapEditorProps> = ({ content, onChange, placeholder, onUploadImage }) => {
  const [imagesEnCours, setImagesEnCours] = useState(0);
  const champFichier = useRef<HTMLInputElement>(null);
  // `useEditor` fige ses options au montage : on passe par une ref pour que
  // le collage et le dépôt voient toujours la fonction courante.
  const televerser = useRef(onUploadImage);
  televerser.current = onUploadImage;
  const editorRef = useRef<Editor | null>(null);

  const insererImages = async (ed: Editor, fichiers: File[], position?: number) => {
    const upload = televerser.current;
    if (!upload) return;
    for (const fichier of fichiers) {
      setImagesEnCours(n => n + 1);
      try {
        const src = await upload(fichier);
        if (!src) continue;
        const contenu = { type: 'image', attrs: { src, alt: fichier.name } };
        if (position !== undefined) {
          ed.chain().focus().insertContentAt(position, contenu).run();
        } else {
          ed.chain().focus().insertContent(contenu).run();
        }
      } finally {
        setImagesEnCours(n => n - 1);
      }
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-info underline',
        },
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      ImageBloc,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-sm max-w-none min-h-[200px] px-4 py-3 focus:outline-none [&_img.ProseMirror-selectednode]:outline [&_img.ProseMirror-selectednode]:outline-2 [&_img.ProseMirror-selectednode]:outline-white/60',
      },
      // Une capture collée ou une photo glissée passe par le téléversement,
      // jamais en base64 dans le HTML.
      handlePaste: (_view, event) => {
        const images = imagesDuTransfert(event.clipboardData);
        if (!images.length || !televerser.current || !editorRef.current) return false;
        event.preventDefault();
        void insererImages(editorRef.current, images);
        return true;
      },
      handleDrop: (view, event) => {
        const images = imagesDuTransfert((event as DragEvent).dataTransfer);
        if (!images.length || !televerser.current || !editorRef.current) return false;
        event.preventDefault();
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
        void insererImages(editorRef.current, images, pos);
        return true;
      },
    },
  });
  editorRef.current = editor;

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL du lien', previousUrl);

    if (url === null) return;

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="border border-border rounded-md overflow-hidden bg-background">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-border bg-card">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive('bold')}
          title="Gras"
        >
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive('italic')}
          title="Italique"
        >
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive('underline')}
          title="Souligné"
        >
          <UnderlineIcon size={16} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive('heading', { level: 2 })}
          title="Titre"
        >
          <Heading2 size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive('heading', { level: 3 })}
          title="Sous-titre"
        >
          <Heading3 size={16} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive('bulletList')}
          title="Liste à puces"
        >
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive('orderedList')}
          title="Liste numérotée"
        >
          <ListOrdered size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive('blockquote')}
          title="Citation"
        >
          <Quote size={16} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          isActive={editor.isActive({ textAlign: 'left' })}
          title="Aligner à gauche"
        >
          <AlignLeft size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          isActive={editor.isActive({ textAlign: 'center' })}
          title="Centrer"
        >
          <AlignCenter size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          isActive={editor.isActive({ textAlign: 'right' })}
          title="Aligner à droite"
        >
          <AlignRight size={16} />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={setLink}
          isActive={editor.isActive('link')}
          title="Lien"
        >
          <LinkIcon size={16} />
        </ToolbarButton>
        {onUploadImage && (
          <>
            <ToolbarButton
              onClick={() => champFichier.current?.click()}
              title="Insérer une image"
            >
              {imagesEnCours > 0 ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
            </ToolbarButton>
            <input
              ref={champFichier}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              className="hidden"
              onChange={(e) => {
                const fichiers = Array.from(e.target.files || []);
                e.target.value = '';
                void insererImages(editor, fichiers);
              }}
            />
          </>
        )}

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          title="Annuler"
        >
          <Undo size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          title="Rétablir"
        >
          <Redo size={16} />
        </ToolbarButton>
      </div>

      {/* Editor content */}
      <EditorContent editor={editor} />
    </div>
  );
};

export default TipTapEditor;
